/*
 * Copyright (c) 2020 Honeywell International Inc. All Rights Reserved.
 * Honeywell and its product names are among the trademarks and/or service marks owned
 * by Honeywell International, Inc., or its subsidiaries.
 *
 * For patent information, see http://www.hsmpats.com.
 */

import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Button, Checkbox, Notification } from '@scuf/common';
import Moment from 'moment';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import validator from 'validator';
import { util } from '@keystone/common';
import Routes from '../../common/routes';
import SLAContainer from './SLAContainer';
import SLAUserInputs from './SLAUserInputs';
import APIClient from '../../common/APIClient';

const downloadPDF = () => {
  fetch('/Honeywell_Voice_SLA.pdf')
    .then((res) => res.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Honeywell_Voice_SLA.pdf';
      document.body.appendChild(a); // append to DOM (for firefox)
      a.click();
      a.remove();
    });
};

/**
 * Class that renders the SLA text, checkbox, and submit button components within
 * our SLAContainer component.
 */
class SLASubmit extends Component {
  constructor(props) {
    super(props);
    this.state = {
      slaLinkId: '',
      checked: false,
      dateAccepted: null,
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      companyName: '',
      city: '',
      streetAddress: '',
      country: '',
      submitError: '',
      validationError: null,
    };
  }

  async componentDidMount() {
    const { location, history } = this.props;
    // parse id parameter
    const searchValues = queryString.parse(location.search);
    const slaLinkId = searchValues.id;
    // make call to check if ID is valid
    try {
      const result = await APIClient.checkSLAId(slaLinkId);
      if (result.response.valid) {
        this.setState({ slaLinkId });
      } else {
        // redirect to invalid ID page
        history.push(Routes.SLAInvalidLink);
      }
    } catch (err) {
      // redirect to invalid ID page
      history.push(Routes.SLAInvalidLink);
    }
  }

  updateValue = (name, value) => {
    this.setState({ [name]: value });
  };

  validate() {
    const {
      firstName,
      lastName,
      email,
      phoneNumber,
      companyName,
      city,
      streetAddress,
      country,
      region,
      dateAccepted,
    } = this.state;
    let valid = false;
    if (
      dateAccepted !== null &&
      validator.isByteLength(firstName, { min: 1, max: 100 }) &&
      validator.isByteLength(lastName, { min: 1, max: 100 }) &&
      validator.isEmail(email) &&
      validator.isMobilePhone(phoneNumber) &&
      validator.isByteLength(companyName, { min: 1, max: 100 }) &&
      validator.isByteLength(city, { min: 1, max: 100 }) &&
      validator.isByteLength(streetAddress, { min: 1, max: 100 }) &&
      validator.isByteLength(country, { min: 1, max: 100 })
    ) {
      valid = true;
      this.setState({ validationError: false });
    } else {
      this.setState({ validationError: true });
    }
    return valid;
  }

  async submit() {
    const {
      slaLinkId,
      firstName,
      lastName,
      email,
      phoneNumber,
      companyName,
      city,
      streetAddress,
      country,
      dateAccepted,
      affiliate,
    } = this.state;
    const customerResponse = {
      firstName,
      lastName,
      email,
      phoneNumber: {
        value: phoneNumber,
        locale: country,
      },
      companyName,
      city,
      streetAddress,
      country,
      dateAccepted: dateAccepted.toISOString(),
      affiliate,
    };
    // verify values
    const valid = this.validate();
    if (valid) {
      // make api call to submit response
      try {
        await APIClient.submitSLA({
          slaLinkId,
          customerResponse,
        });
        return true;
      } catch (err) {
        this.setState({ submitError: err.message });
        return false;
      }
    }
    return false;
  }

  renderDateChecked() {
    const { dateAccepted } = this.state;
    if (dateAccepted !== null) {
      return (
        <div style={{ padding: '1rem' }}>
          <b>Date SLA Accepted:</b> {dateAccepted.format('LLL')}
        </div>
      );
    }
    return (
      <div style={{ padding: '1rem' }}>
        <b>SLA Not Accepted</b>
      </div>
    );
  }

  renderError = (field, fieldTitle, touched) => {
    const { [field]: value, validationError } = this.state;
    if (touched || validationError) {
      if (field === 'email' && !validator.isEmail(value)) {
        return `${fieldTitle} must be a valid email address`;
      }
      if (field === 'phoneNumber' && !validator.isMobilePhone(value)) {
        return `${fieldTitle} must be a valid phone number`;
      }
      if (validator.isEmpty(value)) {
        return `${fieldTitle} must not be blank`;
      }
      if (!validator.isByteLength(value, { min: 1, max: 100 })) {
        return `${fieldTitle} must be less than 100 characters`;
      }
    }
    return '';
  };

  renderFailedSubmit() {
    const { submitError } = this.state;
    if (submitError) {
      return (
        <Notification hasIcon severity="critical" style={{ width: 'unset' }}>
          {submitError}
        </Notification>
      );
    }
    return <></>;
  }

  renderValidationError() {
    const { validationError } = this.state;
    if (validationError) {
      return (
        <Notification hasIcon severity="critical" style={{ width: 'unset' }}>
          Error submitting form, invalid values have been provided
        </Notification>
      );
    }
    return <></>;
  }

  render() {
    const { history } = this.props;
    const { checked, slaLinkId } = this.state;
    return (
      <SLAContainer>
        <div>
          <h3>Thank you for your purchase of Honeywell Voice.</h3>
        </div>
        <div>
          Please fill out the form below and accept the SLA. Once completed we will issue you with
          your license so you can get started.
        </div>
        <div>
          You can read the full contents of the SLA or download it as a PDF document on your device.
        </div>
        <div style={{ padding: '.5rem' }}>
          <a
            onClick={() => null}
            href={`${util.getAPIGatewayURL()}/sla/getSLA?slaLinkId=${slaLinkId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button type="primary" content="Download PDF" onClick={() => {}} />
          </a>
        </div>
        <SLAUserInputs onChange={this.updateValue} renderError={this.renderError} />
        <Notification hasIcon severity="important" style={{ width: 'unset' }}>
          By checking the box you agree that you are authorized to sign on behalf of your company.
        </Notification>
        <Checkbox
          label="Check this box to accept the SLA"
          checked={checked}
          onChange={(value) => {
            this.setState({ checked: value });
            if (value) {
              this.setState({ dateAccepted: new Moment() });
            } else {
              this.setState({ dateAccepted: null });
            }
          }}
        />
        {this.renderDateChecked()}
        <Button
          type="primary"
          content="Submit"
          disabled={!checked}
          onClick={async () => {
            const result = await this.submit();
            if (result) {
              history.push(Routes.SLASuccess);
            }
          }}
        />
        {this.renderFailedSubmit()}
        {this.renderValidationError()}
      </SLAContainer>
    );
  }
}

SLASubmit.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
};

export default withRouter(SLASubmit);
